import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import LogoMark from "@/components/site/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        const v = email.trim();
        if (!v) {
            toast.error("Please enter your email.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/subscribe`, { email: v });
            toast.success("Subscribed. Thank you.");
            setEmail("");
        } catch (err) {
            const detail =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Could not subscribe. Please try again.";
            toast.error(
                typeof detail === "string" ? detail : "Please enter a valid email.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="newsletter"
            data-testid="newsletter-section"
            className="py-20 md:py-28 bg-bone2 grain relative overflow-hidden"
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid grid-cols-12 gap-6 md:gap-12 items-center">
                    <div className="col-span-12 lg:col-span-7">
                        <div className="flex items-center gap-4 mb-6">
                            <LogoMark size="w-12 h-12" />
                            <span className="eyebrow text-ink2">
                                Letters from Country
                            </span>
                        </div>

                        <h2
                            data-testid="newsletter-heading"
                            className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink max-w-2xl"
                        >
                            Slow notes from the
                            <br />
                            <em className="italic font-light text-eucalyptus">
                                work on Country.
                            </em>
                        </h2>

                        <p className="mt-6 text-ink2 text-lg leading-relaxed max-w-xl">
                            Occasional dispatches on Indigenous-led
                            stewardship, restoration, ranger programs, native
                            seed, cultural fire and the systems holding it all
                            together.
                        </p>
                    </div>

                    <div className="col-span-12 lg:col-span-5 lg:pl-8">
                        <form
                            onSubmit={onSubmit}
                            data-testid="newsletter-form"
                            className="border-t border-rule pt-8"
                            noValidate
                        >
                            <label className="block">
                                <span className="eyebrow text-ink2">
                                    Your email
                                </span>
                                <div className="mt-2 flex items-end gap-4 border-b border-ink/30 focus-within:border-ochre transition-colors">
                                    <input
                                        data-testid="newsletter-email"
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        placeholder="you@organisation.org"
                                        className="flex-1 bg-transparent border-0 px-0 py-3 text-ink placeholder:text-ink2/50 focus:outline-none"
                                        required
                                    />
                                    <button
                                        data-testid="newsletter-submit"
                                        type="submit"
                                        disabled={loading}
                                        className="shrink-0 px-5 py-3 bg-ink text-bone text-[11px] tracking-[0.22em] uppercase hover:bg-eucalyptus transition-colors disabled:opacity-60 -mb-px"
                                    >
                                        {loading ? "…" : "Subscribe"}
                                    </button>
                                </div>
                            </label>
                            <p className="mt-4 text-xs text-ink2/80 leading-relaxed max-w-md">
                                No spam, ever. Unsubscribe anytime. We respect
                                your data.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
